import React from 'react';
import { heroData } from '../data/mock';
import { ArrowLeft } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useParallax from '../hooks/useParallax';

const HeroSection = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.1 });
  const [subtitleRef, subtitleVisible] = useScrollAnimation({ threshold: 0.1 });
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });
  
  // Parallax effects
  const parallaxGrid = useParallax(0.15, 'down');
  const parallaxGradient = useParallax(0.1, 'up');

  return (
    <section className="min-h-screen bg-[#FDF8FA] relative overflow-hidden flex items-center">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#C5366A]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#3D4B8C]/10 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-[7.6923%] w-full relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 pt-32 pb-20">
          {/* Text Content */}
          <div className="flex-1 text-right" dir="rtl">
            {/* Top Headline */}
            <p 
              ref={titleRef}
              className={`text-[#C5366A] text-lg md:text-xl font-medium mb-6 tracking-wide transition-all duration-700 ${
                titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {heroData.topHeadline}
            </p>

            {/* Main Headline */}
            <h1 
              ref={subtitleRef}
              className={`text-[#2D2D2D] text-4xl md:text-5xl lg:text-[66px] font-semibold leading-[1.1] mb-8 transition-all duration-700 delay-100 ${
                subtitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {heroData.mainHeadline}
            </h1>

            {/* Sub Headline */}
            <p 
              className={`text-[#6B6B6B] text-lg md:text-xl leading-relaxed mb-12 max-w-2xl transition-all duration-700 delay-200 ${
                subtitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {heroData.subHeadline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a
                ref={ctaRef}
                href="#solutions"
                className={`inline-flex items-center justify-between gap-3 bg-[#C5366A] text-white px-8 py-4 text-lg font-medium rounded-lg hover:bg-[#A12D56] transition-all duration-400 ease-in-out min-h-[56px] group shadow-lg shadow-[#C5366A]/30 ${
                  ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '300ms' }}
              >
                <span>{heroData.ctaText}</span>
                <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
              </a>
              <a
                href="#about"
                className={`inline-flex items-center justify-between gap-3 bg-[#E8EEF8] text-[#3D4B8C] px-8 py-4 text-lg font-medium rounded-lg hover:bg-[#D8DEE8] transition-all duration-400 ease-in-out min-h-[56px] ${
                  ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '400ms' }}
              >
                מידע נוסף
              </a>
            </div>
          </div>

          {/* Animated Gradient Orbs - Replacing dark Spline */}
          <div className="flex-1 hidden lg:flex items-center justify-center" style={{ minHeight: '600px', position: 'relative' }}>
            {/* Main animated gradient sphere */}
            <div className="relative w-[400px] h-[400px]">
              {/* Outer glow */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#C5366A]/20 to-[#3D4B8C]/20 blur-3xl animate-pulse" />
              
              {/* Main sphere */}
              <div 
                className="absolute inset-8 rounded-full bg-gradient-to-br from-[#E8A4B8] via-[#C5366A] to-[#3D4B8C] shadow-2xl"
                style={{
                  animation: 'float 6s ease-in-out infinite',
                  boxShadow: '0 25px 80px -20px rgba(197, 54, 106, 0.5), inset -20px -20px 60px rgba(255,255,255,0.3), inset 20px 20px 60px rgba(61, 75, 140, 0.3)'
                }}
              />
              
              {/* Inner highlight */}
              <div 
                className="absolute top-12 left-12 w-24 h-24 rounded-full bg-gradient-to-br from-white/60 to-transparent blur-sm"
                style={{ animation: 'float 6s ease-in-out infinite reverse' }}
              />
              
              {/* Floating small orbs */}
              <div 
                className="absolute -top-8 -right-8 w-20 h-20 rounded-full bg-gradient-to-br from-[#E8A4B8] to-[#C5366A] opacity-60"
                style={{ animation: 'orbit 8s linear infinite', boxShadow: '0 10px 40px -10px rgba(197, 54, 106, 0.5)' }}
              />
              <div 
                className="absolute -bottom-4 -left-12 w-16 h-16 rounded-full bg-gradient-to-br from-[#3D4B8C] to-[#5B4B8C] opacity-50"
                style={{ animation: 'orbit 10s linear infinite reverse', boxShadow: '0 10px 30px -10px rgba(61, 75, 140, 0.5)' }}
              />
              <div 
                className="absolute top-1/2 -right-16 w-12 h-12 rounded-full bg-gradient-to-br from-[#F5E6EC] to-[#E8A4B8] opacity-70"
                style={{ animation: 'float 4s ease-in-out infinite', animationDelay: '1s' }}
              />
            </div>
            
            {/* CSS Keyframes */}
            <style>{`
              @keyframes float {
                0%, 100% { transform: translateY(0) rotate(0deg); }
                50% { transform: translateY(-20px) rotate(5deg); }
              }
              @keyframes orbit {
                from { transform: rotate(0deg) translateX(30px) rotate(0deg); }
                to { transform: rotate(360deg) translateX(30px) rotate(-360deg); }
              }
            `}</style>
          </div>
        </div>
      </div>

      {/* Gradient Overlay at Bottom */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FDF8FA] to-transparent pointer-events-none" 
      />
    </section>
  );
};

export default HeroSection;
